import {
  createContext,
  useContext,
  useState,
  useEffect,
  Dispatch,
  useMemo,
  SetStateAction,
  PropsWithChildren,
} from "react";
import { useCoreApiClient } from "@api/use-core-api-client";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useAppParams } from "@router/router-helper";
import {
  APITypes,
  CoreAPITypes,
  CoreAPIUtils,
  SphereDashboardAPITypes,
} from "@stellar/api-logic";
import { BaseProjectProps } from "@custom-types/project-types";
import { useAppSelector } from "@store/store-helper";
import { currentUserSelector } from "@store/user/user-selector";
import { isProjectDemo } from "@utils/project-utils";
import { isAllowedToChangeManagement } from "@pages/project-details/project-overview/change-management/change-management-utils";
import { isProjectArchiveSelector } from "@store/projects/projects-selector";

const NOT_AUTHORIZED = 401;

interface GroupMembersContextType extends BaseProjectProps {
  /** List of members belonging to the selected group */
  groupMembers: SphereDashboardAPITypes.IGroupMember[];

  /** Method to manipulate the selected group id */
  setSelectedGroupId: Dispatch<SetStateAction<APITypes.GroupId | undefined>>;

  /** ID of the selected group whose members should be fetched */
  selectedGroupId?: APITypes.GroupId;

  /** Whether the current user is allowed to modify project management */
  canChangeManagement: boolean;
}

// Context that provides the state of the group members
const GroupMembersContext = createContext<GroupMembersContextType | undefined>(
  undefined
);

/**
 * Provider that manages the state of the members of a specific group
 * based on the provided group ID.
 *
 * It automatically fetches the group members whenever the selected group ID changes,
 * and stores the data accordingly.
 */
export function GroupMembersProvider({
  project,
  children,
}: PropsWithChildren<BaseProjectProps>): JSX.Element {
  const { companyId } = useAppParams();
  const coreApiClient = useCoreApiClient();
  const { handleErrorWithToast } = useErrorContext();

  const currentUser = useAppSelector(currentUserSelector);

  const isProjectArchived = useAppSelector(
    isProjectArchiveSelector(project.id)
  );

  const [groupMembers, setGroupMembers] = useState<
    SphereDashboardAPITypes.IGroupMember[]
  >([]);
  const [selectedGroupId, setSelectedGroupId] = useState<
    APITypes.GroupId | undefined
  >(project?.group?.id);

  useEffect(() => {
    async function onGroupSelected(groupId: string | undefined): Promise<void> {
      if (!companyId || !groupId) {
        return undefined;
      }

      setGroupMembers([]);

      try {
        const { members } = await coreApiClient.V3.SDB.getGroupById(
          companyId,
          groupId
        );
        setGroupMembers(members);
      } catch (error) {
        if (
          CoreAPIUtils.isResponseError(error) &&
          error.status === NOT_AUTHORIZED
        ) {
          setGroupMembers([]);
        } else {
          handleErrorWithToast({
            id: `getGroupById-${Date.now().toString()}`,
            title: "Cannot get the group details",
            error,
          });
        }
      }
    }

    const isRestrictedRole =
      currentUser?.role === CoreAPITypes.EUserCompanyRole.projectManager ||
      currentUser?.role === CoreAPITypes.EUserCompanyRole.member;

    if (!isRestrictedRole) {
      void onGroupSelected(selectedGroupId?.toString());
    }
  }, [
    companyId,
    selectedGroupId,
    handleErrorWithToast,
    coreApiClient,
    currentUser,
  ]);

  const canChangeManagement = useMemo(() => {
    // Prevent enabling management changes until all required data is loaded
    // Returns false by default while waiting for project, current user, or group members to be available
    if (!project || !currentUser || groupMembers.length === 0) {
      return false;
    }

    const isDemo = project && isProjectDemo(project);

    // If the project is a demo project, no one can change management
    // If the user is a company executive, they can change management
    if (
      !isDemo &&
      currentUser.role === CoreAPITypes.EUserCompanyRole.companyExecutive
    ) {
      return true;
    }

    const projectManager = project.managers.projectManager;

    if (!projectManager) {
      return false;
    }

    const currentUserMember = groupMembers.find(
      (member) => member.identity === currentUser.identity
    );

    // Project manager or group viewer can't access the change management
    // Only group manager can access the change management
    if (
      currentUserMember?.role ===
        CoreAPITypes.EUserCompanyRole.projectManager ||
      currentUserMember?.role === CoreAPITypes.EUserCompanyRole.groupViewer
    ) {
      return false;
    }

    return isAllowedToChangeManagement(isProjectArchived, isDemo, project);
  }, [currentUser, groupMembers, isProjectArchived, project]);

  return (
    <GroupMembersContext.Provider
      value={{
        project,
        groupMembers,
        setSelectedGroupId,
        selectedGroupId,
        canChangeManagement,
      }}
    >
      {children}
    </GroupMembersContext.Provider>
  );
}

/**
 * Hook to consume the group members context.
 * Throws an error if used outside of the GroupMembersProvider context.
 */
export function useGroupMembersContext(): GroupMembersContextType {
  const context = useContext(GroupMembersContext);
  if (!context) {
    throw new Error(
      "useGroupMembersContext must be used within a GroupMembersProvider"
    );
  }

  return context;
}
